<template>
  <div>
    <v-card class="px-3" color="white" flat>        
      <v-container class="px-5 pt-5" style="overflow: hidden;">
          <v-row class="pt-5" justify="space-between" align="center">
            <v-btn fab small 
              color=primaryblue30 
              depressed 
              id="age-decrement-button" 
              @click="decrement"
              :disabled="this.input <= this.min"
            >
              <v-icon size=36 color=primaryblue>{{ icons.mdiMinus }}</v-icon>
            </v-btn>

            <v-row class="mx-5" style="position: relative;">
              
              <v-slider
                id="age-slider"
                class="pt-5 mx-0"
                v-model="input"
                :max="sliderMaximum"
                @input="setValue"
                thumb-color=primaryblue
                color=lightblue
                track-color=primaryblue30
                thumb-size="0"
                thumb-label="always"
              >
                <template v-slot:thumb-label="item">
                    <h1 class="info-thumb">
                    <mrx-translation
                      :token="ageType"
                      class="age-type"
                      :style="ageTypeStyle"
                    ></mrx-translation>
                    <mrx-translation style="font-size: 20px; font-weight: 600; color: var(--primary-blue);  white-space: nowrap;"
                      :prepend="displayValueTranslation" 
                      :token="displayUnit"></mrx-translation>
                    </h1>
                </template>
              </v-slider>

              <div v-for="divider in dividers" :key="divider">
                <div class="range-division range-division-top" :style="{left: divider + '%'}"></div>
                <div class="range-division range-division-bottom" :style="{left: divider + '%'}"></div>
              </div>
            </v-row>

            <v-btn fab small 
              color=primaryblue30 
              depressed
              id="age-increment-button"
              @click="increment"
              :disabled="this.input >= this.sliderMaximum"
            >
              <v-icon size=36 color=primaryblue>{{ icons.mdiPlus }}</v-icon>
            </v-btn>
          </v-row>        
        </v-container>      
    </v-card>
  </div>
</template>

<script>
  import { convert, convertFromDays } from '@/utils/conversions'
  import ageRanges from '@/mixins/ageRanges'
  import languageLogic from '@/mixins/languageLogic'
  import icons from '@/mixins/icons'

  export default {
    name: 'ageslider',
    mixins: [ageRanges, languageLogic, icons],
    model: {
      prop: 'items',
      event: 'click'
    },
    data() {
      return {
        input: null,
        min: 0,
        numberDays: 60,
        numberMonths: 58,
        numberYears: 95,
        unit: 'days',
        displayValue: 0,
        actualValueDays: 0,
        ageTypeStyle: ""
      }
    },
    props: {
      'options': Object,
      'items': Array,
      'panelName': String
    },
    methods: {
      increment() {
        if (this.input + 1 <= this.sliderMaximum) this.input += 1
        this.setValue()
        this.$track(this.$analyticsEvents.SLIDER_INCREMENT, {
          component: 'AgeSlider',
          panel_name: this.panelName,
          value: this.actualValueDays,
          display_value: this.displayValue,
          unit: this.unit
        })
      },
      decrement() {
        if (this.input - 1 >= this.min) this.input -= 1
        this.$nextTick(() => { this.setValue() })
        this.$track(this.$analyticsEvents.SLIDER_DECREMENT, {
          component: 'AgeSlider',
          panel_name: this.panelName,
          value: this.actualValueDays,
          display_value: this.displayValue,
          unit: this.unit
        })
      },
      setValue() {
        if (this.input < this.min || this.input === null) {
          this.items[0].selected = false
          this.items[0].value = null
        }
        else {
          this.actualValueDays = this.getAgeFromTickPosition(this.input, 'days')
          this.unit = this.getSliderUnit(this.actualValueDays)
          this.displayValue = this.getAgeFromTickPosition(this.input, this.unit)

          this.items[0].value = parseInt(this.actualValueDays)
          this.items[0].selected = true
          this.$track(this.$analyticsEvents.SLIDER_CHANGE, {
            component: 'AgeSlider',
            panel_name: this.panelName,
            value: this.actualValueDays,
            display_value: this.displayValue,
            unit: this.unit
          })
        }
      },
      setInitial() {
        var existingValue = this.items[0].value

        // if value already exists, must convert back from age in days (existing value) to number of ticks on slider
        if (!existingValue) {
          this.input = this.min - 1
        } else {
          this.unit = this.getSliderUnit(existingValue)
          this.input = this.getTickPositionFromDays(existingValue)
          this.displayValue = (this.unit === 'days') ? existingValue : convertFromDays(this.unit, 0, existingValue)
        }
      }, 
      getSliderUnit(ageInDays) {
        // returns the unit that the age in days falls into based on slider settings

        if (ageInDays < this.numberDays) {
            return 'days'
          } else if (ageInDays < this.numberDays + parseInt(convert('months', 'days', this.numberMonths))) {
            return 'months'
          } else {
            return 'years'
          }
      },
      getTickPositionFromDays(ageInDays) {
        // returns the number of ticks corresponding to age in days based on slider settings

        var unit = this.getSliderUnit(ageInDays)
        if (unit === 'days') {
          return ageInDays
        } else if (unit === 'months') {
          return parseInt(convertFromDays('months', 0, ageInDays - this.numberDays)) + this.numberDays
        } else {
          return parseInt(convertFromDays('years', 0, ageInDays - this.numberDays - parseInt(convert('months', 'days', this.numberMonths)))) 
            + this.numberMonths + this.numberDays
        }
      },
      getAgeFromTickPosition(ticks, unit) {
        // returns the age in days, months, or years corresponding to the current tick position

        if (ticks <= this.numberDays ) {
          return parseInt(convert('days', unit, ticks))
        } else if (ticks < this.numberDays + this.numberMonths) { 
          var months = ticks - this.numberDays + parseInt(convert('days', 'months', this.numberDays, 0))
          return parseInt(convert('months', unit, months))
        } else {
          var monthsSectionInDays = parseInt(convert('months', 'days', this.numberMonths))
          var years = ticks - (this.numberDays + this.numberMonths) + parseInt(convert('days', 'years', this.numberDays + monthsSectionInDays, 0))
          return parseInt(convert('years', unit, years))
        }
      }
    },
    computed: {
      ageType() {
        var ageType = this.ageTypeByDays(this.items[0].value)
        return `demographics.age.${ageType}`
      },
      sliderMaximum() {
        return this.numberDays + this.numberMonths + this.numberYears
      },
      displayUnit() {
        return this.unit ? `units.${this.unit}.short` : ''
      },
      displayUnitDays() {
        return this.unit ? `units.days.short` : ''
      },
      displayValueTranslation() {
        return this.$i18n.n(this.displayValue, 'decimal').toString()
      },
      displayValueDays() {
        return this.actualValueDays < this.min ? null : this.$i18n.n(this.actualValueDays, 'decimal').toString()
      },
      dividers() {
        // returns percentage locations of age dividers
        var ranges = this.getAgeRanges()
        var childTick = this.getTickPositionFromDays(convert(ranges.child.unit, 'days', ranges.child.min))
        var youthTick = this.getTickPositionFromDays(convert(ranges.youth.unit, 'days', ranges.youth.min))
        var adultTick = this.getTickPositionFromDays(convert(ranges.adult.unit, 'days', ranges.adult.min))

        return [(childTick/this.sliderMaximum) * 100, (youthTick/this.sliderMaximum) * 100, (adultTick/this.sliderMaximum) * 100]
      }
    },
    created() {
      this.setInitial()
    },
    watch: {
      panelName: function() {
        this.setInitial()
      },
      ageType: function() {
          this.ageTypeStyle = "animation: font-size-transition 500ms;"

          // remove animation after finished so it can be re-applied
          setTimeout(() =>  this.ageTypeStyle = "", 500)
      }
    }
  }
</script>

<style>

  .info-thumb {
    transform: translateY(-35px);
    -webkit-transform: translateY(-35px);
    -moz-transform: translateY(-35px);
    -o-transform: translateY(-35px);
    -ms-transform: translateY(-35px);
    text-align:center;
    line-height: 80%;
  }

  .age-type {
    font-size: 12px;
    color: var(--primary-blue);
    opacity: 0.5;
    text-transform: uppercase;
  }

  .range-division {
    height: 12px;
    width: 1px;
    position: absolute;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -moz-transform: translate(-50%, -50%);
    -o-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    background-color: var(--primary-blue);
    display: inline-block;
  }

  .range-division-top {
    top: calc(50% - 11px);
  }

  .range-division-bottom {
    top: calc(50% + 9px);
  }

  @keyframes font-size-transition {
    0% {
      font-size: 12px;
    }
    50% {
      font-size: 14px;
    }
    100% {
      font-size: 12px;
    }
  }
</style>
